import { format } from 'date-fns';
import { employees as seedEmployees } from '../data/employees';
import { holidays as seedHolidays } from '../data/holidays';
import { selfDocs as seedSelfDocs, selfRequests } from '../data/selfService';
import { policies as seedPolicies, ptoRequests } from '../data/timeOff';
import type { DocFolder, Employee, Policy, ProfileDetails, PtoRequest, RecentFile } from '../types/hr';
import type { ChangeRequest, Holiday, SelfDoc } from '../types/selfService';
import { TODAY } from './format';

/** Session-only, in-memory app state (no backend). */
export interface HRState {
  employees: Employee[];
  policies: Policy[];
  requests: PtoRequest[];
  details: Record<string, ProfileDetails>;
  files: Record<string, RecentFile[]>;
  folders: Record<string, DocFolder[]>;
  holidays: Holiday[];
  changeRequests: ChangeRequest[];
  selfDocs: SelfDoc[];
}

const TODAY_ISO = format(TODAY, 'yyyy-MM-dd');

let state: HRState = {
  employees: seedEmployees,
  policies: seedPolicies,
  requests: [...ptoRequests, ...selfRequests],
  details: {},
  files: {},
  folders: {},
  holidays: seedHolidays,
  changeRequests: [],
  selfDocs: seedSelfDocs,
};

const listeners = new Set<() => void>();

export function getHRState(): HRState {
  return state;
}

export function subscribeHR(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

function update(updater: (s: HRState) => HRState) {
  state = updater(state);
  listeners.forEach((l) => l());
}

function patchRequest(id: string, patch: Partial<PtoRequest>) {
  update((s) => ({ ...s, requests: s.requests.map((r) => (r.id === id ? { ...r, ...patch } : r)) }));
}

export const hrActions = {
  updateEmployee(id: string, patch: Partial<Employee>) {
    update((s) => ({ ...s, employees: s.employees.map((e) => (e.id === id ? { ...e, ...patch } : e)) }));
  },
  saveDetails(id: string, details: ProfileDetails) {
    update((s) => ({ ...s, details: { ...s.details, [id]: details } }));
  },
  decideRequest(id: string, status: 'approved' | 'denied') {
    patchRequest(id, { status, approver: 'Jordan Avery', decidedOn: TODAY_ISO, decidedInSession: true });
  },
  cancelRequest(id: string) {
    patchRequest(id, { status: 'cancelled', decidedOn: TODAY_ISO });
  },
  restoreRequest(id: string) {
    patchRequest(id, { status: 'pending', decidedOn: undefined });
  },
  addRequest(request: PtoRequest) {
    update((s) => ({ ...s, requests: [request, ...s.requests] }));
  },
  savePolicy(policy: Policy) {
    update((s) => ({
      ...s,
      policies: s.policies.some((p) => p.id === policy.id) ? s.policies.map((p) => (p.id === policy.id ? policy : p)) : [...s.policies, policy],
    }));
  },
  addFiles(employeeId: string, files: RecentFile[]) {
    update((s) => ({ ...s, files: { ...s.files, [employeeId]: [...files, ...(s.files[employeeId] ?? [])] } }));
  },
  addFolder(employeeId: string, folder: DocFolder) {
    update((s) => ({ ...s, folders: { ...s.folders, [employeeId]: [...(s.folders[employeeId] ?? []), folder] } }));
  },
  saveHoliday(holiday: Holiday) {
    update((s) => ({
      ...s,
      holidays: s.holidays.some((h) => h.id === holiday.id) ? s.holidays.map((h) => (h.id === holiday.id ? holiday : h)) : [...s.holidays, holiday],
    }));
  },
  deleteHoliday(id: string) {
    update((s) => ({ ...s, holidays: s.holidays.filter((h) => h.id !== id) }));
  },
  addChangeRequest(request: ChangeRequest) {
    update((s) => ({ ...s, changeRequests: [request, ...s.changeRequests] }));
  },
  completeChangeRequest(id: string) {
    update((s) => ({ ...s, changeRequests: s.changeRequests.map((c) => (c.id === id ? { ...c, status: 'completed' } : c)) }));
  },
  addSelfDocs(docs: SelfDoc[]) {
    update((s) => ({ ...s, selfDocs: [...docs, ...s.selfDocs] }));
  },
  deleteSelfDoc(id: string) {
    update((s) => ({ ...s, selfDocs: s.selfDocs.filter((d) => d.id !== id || d.uploadedBy !== 'Me') }));
  },
  signSelfDoc(id: string) {
    update((s) => ({
      ...s,
      selfDocs: s.selfDocs.map((d) =>
        d.id === id ? { ...d, needsSignature: false, folder: 'signed', signedVia: 'DocuSign', name: `${d.name} — signed.pdf`, date: TODAY_ISO } : d,
      ),
    }));
  },
};
